import React, { useState, useRef, useEffect } from 'react';
import { MdClose, MdSend, MdSmartToy } from 'react-icons/md';
import ChatService from '../../services/chat';
import { Link } from 'react-router-dom';
import { formatPrice } from 'utils/formatPrice';
import { tEnum, useTranslation } from '../../i18n';
import { productPath } from 'utils/storePaths';
import { BRAND } from 'config/brand';

const Chatbot = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [sessionId, setSessionId] = useState(() => ChatService.readStoredSessionId());
    const [messages, setMessages] = useState([
        {
            id: 1,
            text: t('chatbot.welcome'),
            sender: 'bot',
            timestamp: new Date()
        }
    ]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, isOpen]);

    // Safety timeout to prevent infinite loading state
    useEffect(() => {
        let timeout;
        if (loading) {
            timeout = setTimeout(() => {
                setLoading(false);
                setMessages(prev => [...prev, {
                    id: Date.now(),
                    text: t('chatbot.unavailable'),
                    sender: 'bot',
                    timestamp: new Date()
                }]);
            }, 15000); // 15 seconds timeout
        }
        return () => clearTimeout(timeout);
    }, [loading, t]);

    const isPasswordRecoveryQuery = (text) => {
        const t = String(text || '').toLowerCase();
        return (
            t.includes('forgot') ||
            t.includes('forget') ||
            t.includes('lost password') ||
            t.includes('lose password') ||
            t.includes('reset password') ||
            t.includes('password reset') ||
            t.includes('recover password') ||
            t.includes('cant login') ||
            t.includes("can't login") ||
            t.includes('unable to login')
        );
    };

    const openAuthModal = (tab = 'signin') => {
        try {
            window.dispatchEvent(new CustomEvent('zaynic:open-auth', { detail: { tab } }));
        } catch (e) {
            // no-op
        }
    };

    const normalizeBotText = (text) => {
        const raw = String(text || '');
        // 1) Strip markdown emphasis (UI renders ** literally)
        let out = raw.replace(/\*\*/g, '');

        // 2) Smarter fallback when bot lacks a specific record
        const fallback =
            // i18n-ignore: the server's exact English fallback, compared below
            "I don't have that specific record in my system from this message, but I can help with " + BRAND.name + " products, orders (after login), subscriptions, or password reset steps. What would you like to try?";
        if (out.trim() === fallback) {
            out = t('chatbot.noRecord');
        }

        return out;
    };

    const handleSend = async () => {
        if (!input.trim()) return;

        const userMessage = {
            id: Date.now(),
            text: input,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        const userText = userMessage.text;
        setInput('');

        // Local intent: forgot/lost password help (no server call)
        if (isPasswordRecoveryQuery(userText)) {
            setMessages(prev => [
                ...prev,
                {
                    id: Date.now() + 1,
                    sender: 'bot',
                    timestamp: new Date(),
                    text: t('chatbot.recovery'),
                    actions: [
                        { id: 'forgot', label: t('authPages.forgotTitle'), type: 'link', to: '/forgot-password' },
                        { id: 'verify', label: t('chatbot.verifyOtp'), type: 'link', to: '/verify-otp' },
                        { id: 'reset', label: t('authPages.resetTitle'), type: 'link', to: '/reset-password' },
                        { id: 'signin', label: t('chatbot.openLogin'), type: 'event', event: 'zaynic:open-auth', detail: { tab: 'signin' } },
                        { id: 'signup', label: t('chatbot.openSignup'), type: 'event', event: 'zaynic:open-auth', detail: { tab: 'register' } },
                    ],
                },
            ]);
            return;
        }

        setLoading(true);

        try {
            const result = await ChatService.sendMessage(userText, {
                sessionId: sessionId || undefined,
            });

            if (result.success) {
                const sid = result.data.session_id;
                if (sid) {
                    ChatService.persistSessionId(sid);
                    setSessionId(sid);
                }
                const botResponse = {
                    id: Date.now() + 1,
                    text: normalizeBotText(result.data.response),
                    sender: 'bot',
                    timestamp: new Date(),
                    products: result.data.products,
                    orderInfo: result.data.order_info
                };
                setMessages(prev => [...prev, botResponse]);
            } else {
                setMessages(prev => [...prev, {
                    id: Date.now() + 1,
                    text: t('chatbot.unavailable'),
                    sender: 'bot',
                    timestamp: new Date()
                }]);
            }
        } catch (error) {
            setMessages(prev => [...prev, {
                id: Date.now() + 1,
                text: t('chatbot.unavailable'),
                sender: 'bot',
                timestamp: new Date()
            }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className="fixed bottom-6 end-6 z-50 font-sans">
            {/* Chat Window */}
            {isOpen && (
                <div className="mb-4 flex h-[500px] w-80 sm:w-96 animate-fade-in-up flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
                    {/* Header */}
                    <div className="flex items-center justify-between bg-gradient-to-r from-slate-900 to-slate-800 p-4 text-white">
                        <div className="flex items-center gap-3">
                            <div className="rounded-full bg-white/20 p-2">
                                <MdSmartToy className="h-4 w-4" aria-hidden />
                            </div>
                            <div>
                                <p className="text-sm font-bold leading-none">{BRAND.assistant}</p>
                                <p className="text-xs text-slate-300">{t('chatbot.online')}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="rounded-full p-1.5 text-white transition hover:bg-white/20"
                            aria-label={t('chatbot.close')}
                        >
                            <MdClose className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 space-y-4 overflow-y-auto bg-gray-50 p-4">
                        {messages.map((msg, index) => (
                            <div key={msg.id || `msg-${index}`} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[85%] ${msg.sender === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                                    <div
                                        className={`rounded-2xl p-3 text-sm shadow-sm ${msg.sender === 'user'
                                            ? 'rounded-ee-none bg-slate-900 text-white'
                                            : 'rounded-es-none border border-gray-100 bg-white text-gray-800'
                                            }`}
                                    >
                                        {msg.text}
                                    </div>

                                    {/* Quick Actions */}
                                    {Array.isArray(msg.actions) && msg.actions.length ? (
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {msg.actions.map((a) => {
                                                if (!a) return null;
                                                if (a.type === 'link' && a.to) {
                                                    return (
                                                        <Link key={a.id || a.label} to={a.to} className="inline-flex no-underline">
                                                            <span className="inline-flex rounded-full border-2 border-primary px-3 py-1 text-[0.75rem] font-extrabold text-amber-800 transition hover:bg-amber-50">
                                                                {a.label}
                                                            </span>
                                                        </Link>
                                                    );
                                                }
                                                if (a.type === 'event' && a.event) {
                                                    return (
                                                        <button
                                                            key={a.id || a.label}
                                                            type="button"
                                                            onClick={() => {
                                                                try {
                                                                    window.dispatchEvent(
                                                                        new CustomEvent(a.event, { detail: a.detail || {} })
                                                                    );
                                                                } catch (e) {
                                                                    openAuthModal(a.detail?.tab || 'signin');
                                                                }
                                                            }}
                                                            className="rounded-full border-2 border-primary px-3 py-1 text-[0.75rem] font-extrabold text-amber-800 transition hover:bg-amber-50"
                                                        >
                                                            {a.label}
                                                        </button>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </div>
                                    ) : null}

                                    {/* Product Recommendations */}
                                    {msg.products && (
                                        <div className="mt-2 w-full space-y-2">
                                            {msg.products.map(product => (
                                                <Link to={productPath(product)} key={product.id} className="block no-underline">
                                                    <div className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 bg-white p-2 shadow-sm transition-colors hover:border-primary/50 hover:shadow-md">
                                                        {product.image && (
                                                            <img src={product.image} alt={product.name} className="h-12 w-12 rounded-md object-cover" />
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <p className="truncate text-sm font-medium text-gray-900">{product.name}</p>
                                                            <p className="text-xs font-bold text-amber-800">{formatPrice(product.price)}</p>
                                                        </div>
                                                    </div>
                                                </Link>
                                            ))}
                                        </div>
                                    )}

                                    {/* Order Info */}
                                    {msg.orderInfo && (
                                        <div className="mt-2 w-full rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
                                            <div className="mb-2 flex items-center justify-between">
                                                <span className="text-xs font-bold text-gray-500">{t('chatbot.orderNo', { number: msg.orderInfo.order_number })}</span>
                                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${msg.orderInfo.status === 'delivered' ? 'bg-green-100 text-green-800' :
                                                    msg.orderInfo.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                                                        'bg-yellow-100 text-yellow-800'
                                                    }`}>
                                                    {tEnum('orderStatus', msg.orderInfo.status, msg.orderInfo.status.toUpperCase())}
                                                </span>
                                            </div>
                                            <p className="text-sm font-bold text-gray-900">{t('chatbot.total', { amount: formatPrice(msg.orderInfo.total) })}</p>
                                        </div>
                                    )}

                                    <span className="px-1 text-[10px] text-gray-400">
                                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {loading && (
                            <div className="flex justify-start">
                                <div className="rounded-2xl rounded-es-none border border-gray-100 bg-white p-3 shadow-sm">
                                    <div className="flex gap-1">
                                        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:0ms]" />
                                        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:150ms]" />
                                        <span className="h-2 w-2 animate-bounce rounded-full bg-gray-400 [animation-delay:300ms]" />
                                    </div>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="border-t border-gray-100 bg-white p-3">
                        {/* R7.7: the AI concierge never sees orders or account data. */}
                        <p className="mb-2 text-[11px] leading-snug text-slate-500" data-testid="chat-privacy-notice">
                            {t('ai.chat.notice')}
                        </p>
                        <div className="flex items-center gap-2">
                            <input
                                type="text"
                                placeholder={t('chatbot.placeholder')}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                                disabled={loading}
                                className="min-w-0 flex-1 rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-sm outline-none ring-primary transition focus:border-primary focus:bg-white focus:ring-2 disabled:opacity-60"
                                aria-label={t('chatbot.message')}
                            />
                            <button
                                type="button"
                                onClick={handleSend}
                                disabled={!input.trim() || loading}
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
                                aria-label={t('chatbot.send')}
                            >
                                <MdSend className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toggle button + idle attention ring */}
            <div className={`absolute bottom-0 end-0 h-14 w-14 ${isOpen ? 'pointer-events-none' : ''}`}>
                {!isOpen ? (
                    <span
                        className="absolute inset-0 rounded-full bg-primary/35 motion-safe:animate-ping motion-reduce:animate-none"
                        aria-hidden
                    />
                ) : null}
                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
                    className={`relative z-10 flex h-14 w-14 items-center justify-center rounded-full bg-slate-900 text-white shadow-lg shadow-slate-900/35 ring-2 ring-white/30 transition-all duration-300 hover:scale-110 hover:bg-slate-800 hover:shadow-xl hover:shadow-slate-900/45 active:scale-95 ${isOpen ? 'pointer-events-none scale-0 opacity-0' : 'scale-100 opacity-100'}`}
                >
                    <MdSmartToy className="h-6 w-6" />
                </button>
            </div>
        </div>
    );
};

export default Chatbot;
